import {
    Box,
    FormControlLabel,
    IconButton,
    Stack,
    Tooltip,
    Typography,
} from "@mui/material";

import IColDef from "components/UI/AcxDataGrid/IColDef";
import { useEffect, useMemo, useRef, useState } from "react";
import AcxDataGridStore from "components/UI/AcxDataGrid/AcxDataGridStore";
import { Search, DeleteOutline, CheckRounded } from "@mui/icons-material";
import theme from "Theme/AppTheme";
import AcxDataGrid from "components/UI/AcxDataGrid/AcxDataGrid";
import AcxButton from "components/UI/AcxButton";
import AcxSwitch from "components/UI/AcxSwitch";
import { useStore } from "utils/useStore";
import { AuthStore } from "stores/AuthStore";
import AcxMainTextField from "components/UI/AcxMainTextField";
import AddButton from "../OrganizationDataSources/components/AddButton";
import AcxDialog from "components/UI/Dialog/AcxDialog";
import { observer } from "mobx-react";
import ClassifierService from "services/ClassifierService";
import { ExtendedMetadataClassifier } from "models/ClassifierModel";
import OrganizationBreadcrumbs from "../OrganizationBreadcrumbs";
import { delay } from "utils/helpers";
import { resultTypeToLabel } from "models/ClassifierModel";
import { ExtendedMetadataDialog } from "./ExtendedMetadataDialog";

const classifierService = new ClassifierService();
const dataGridStore = new AcxDataGridStore(
    "OrganizationMetadataManagement",
    "Administrative",
);

dataGridStore.hideFilter = true;
dataGridStore.hideHeader = true;
dataGridStore.hideVertIcon = true;
dataGridStore.disableRowSelectionOnClick = true;
dataGridStore.disableLocalConfig = true;
dataGridStore.isRowSelectable = (params) => params.row.isActive !== false;
dataGridStore.setRows([]);

const StatusCell = ({
    isActive,
    activeText,
    inactiveText,
    activeColor,
    inactiveColor,
}: {
    isActive: boolean;
    activeText: string;
    inactiveText: string;
    activeColor: { bg?: string; text?: string };
    inactiveColor: { bg?: string; text?: string };
}) => (
    <Typography
        sx={{
            height: "100%",
            display: "flex",
            alignItems: "center",
            background: isActive ? activeColor.bg : inactiveColor.bg,
            color: isActive ? activeColor.text : inactiveColor.text,
            mx: -3,
            pl: 3,
            fontSize: 12,
        }}
    >
        {isActive ? activeText : inactiveText}
    </Typography>
);

const OrganizationMetadataManagement = (props) => {
    const authStore = useStore(AuthStore);
    const canEditDataSources = authStore.canUserEdit("Data Sources");
    const canViewMetadataManagement = authStore.canUserView(
        "Metadata Management",
    );
    const isViewOnly = canViewMetadataManagement && !canEditDataSources;
    const [searchValue, setSearchValue] = useState("");
    const [extendedMetadata, setExtendedMetadata] = useState<
        ExtendedMetadataClassifier[]
    >([]);
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [showFieldModal, setShowFieldModal] = useState(false);
    const [currentField, setCurrentField] =
        useState<ExtendedMetadataClassifier | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [showDeactivated, setShowDeactivated] = useState(false);
    const hasFetchedInactiveRef = useRef(false);

    const openEditFieldModal = (field: ExtendedMetadataClassifier) => {
        setCurrentField(field);
        setShowFieldModal(true);
    };

    const handleCloseDialog = async () => {
        setShowFieldModal(false);
        await delay(150);
        setCurrentField(null);
    };

    const handleFieldSaved = (savedField: ExtendedMetadataClassifier) => {
        setExtendedMetadata((prev) => {
            const exists = prev.some((field) => field.id === savedField.id);
            const updated = exists
                ? prev.map((field) =>
                      field.id === savedField.id ? savedField : field,
                  )
                : [...prev, savedField].sort((a, b) =>
                      a.name.localeCompare(b.name),
                  );
            dataGridStore.setRows(updated);
            return updated;
        });
    };

    const columns: IColDef[] = useMemo(() => {
        const baseColumns: IColDef[] = [
            {
                headerName: "Field Name",
                field: "name",
                flex: 3,
                renderCell: (params) => (
                    <AcxButton
                        variant="text"
                        containerStyle={{ height: "100%" }}
                        sx={{
                            height: "100%",
                            mx: -1,
                            justifyContent: "start",
                            fontSize: 12,
                        }} // Adjusted to line up with the column header
                        onClick={() => {
                            openEditFieldModal(params.row);
                        }}
                    >
                        {params.row.name}
                    </AcxButton>
                ),
            },
            {
                headerName: "Description",
                field: "description",
                flex: 3,
                renderCell: (params) =>
                    params.row.description ? (
                        <Tooltip title={params.row.description}>
                            <Box
                                sx={{
                                    height: "100%",
                                    width: "100%",
                                    minWidth: 0,
                                    display: "flex",
                                    alignItems: "center",
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontSize: 12,
                                        width: "100%",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    {params.row.description}
                                </Typography>
                            </Box>
                        </Tooltip>
                    ) : (
                        ""
                    ),
            },
            {
                headerName: "Result Type",
                field: "resultType",
                flex: 1,
                renderCell: (params) =>
                    resultTypeToLabel[params.row.resultType]?.toLowerCase() ||
                    "",
            },
            {
                headerName: "Groupable",
                field: "isGroupable",
                flex: 1,
                renderCell: (params) =>
                    params.row.isGroupable ? "Groupable" : "Not Groupable",
            },
            {
                headerName: "Reportable",
                field: "isIndexed",
                flex: 1,
                renderCell: (params) =>
                    params.row.isIndexed ? (
                        <Stack
                            sx={{
                                height: "100%",
                                justifyContent: "center",
                            }}
                        >
                            <CheckRounded
                                sx={{
                                    width: "20px",
                                    height: "20px",
                                    color: theme.palette.primary.main,
                                }}
                            />
                        </Stack>
                    ) : (
                        ""
                    ),
            },
            {
                headerName: "Is Published",
                field: "isPublished",
                flex: 1,
                renderCell: (params) => (
                    <StatusCell
                        isActive={params.row.isPublished}
                        activeText="Published"
                        inactiveText="Not Published"
                        activeColor={{
                            bg: theme.palette.primary[100],
                            text: theme.palette.primary[500],
                        }}
                        inactiveColor={{
                            bg: theme.palette.warning[100],
                            text: theme.palette.warning[500],
                        }}
                    />
                ),
            },
        ];

        if (showDeactivated) {
            baseColumns.push({
                headerName: "Is Active",
                field: "isActive",
                flex: 1,
                renderCell: (params) => (
                    <StatusCell
                        isActive={params.row.isActive !== false}
                        activeText="Active"
                        inactiveText="Inactive"
                        activeColor={{
                            bg: theme.palette.success[100],
                            text: theme.palette.success[600],
                        }}
                        inactiveColor={{
                            bg: theme.palette.gray[100],
                            text: theme.palette.gray[600],
                        }}
                    />
                ),
            });
        }

        return baseColumns;
    }, [showDeactivated]);

    useEffect(() => {
        const hasMetadataAccess =
            canEditDataSources || canViewMetadataManagement;

        dataGridStore.checkboxSelection = !isViewOnly;
        dataGridStore.isRowSelectable = isViewOnly
            ? () => false
            : (params) => params.row.isActive !== false;

        if (isViewOnly) {
            dataGridStore.clearSelected();
        }

        const getExtendedMetadata = async () => {
            dataGridStore.setLoading(true);
            const metadata =
                await classifierService.getAllExtendedMetadataClassifiers();
            setExtendedMetadata(metadata);
            dataGridStore.setLoading(false);
        };

        if (hasMetadataAccess) {
            getExtendedMetadata();
        }
    }, [canEditDataSources, canViewMetadataManagement, isViewOnly]);

    useEffect(() => {
        dataGridStore.setColumns(columns);
    }, [columns]);

    useEffect(() => {
        if (showDeactivated && !hasFetchedInactiveRef.current) {
            const getInactiveMetadata = async () => {
                dataGridStore.setLoading(true);
                const metadata =
                    await classifierService.getAllExtendedMetadataClassifiers(
                        true,
                    );
                setExtendedMetadata(metadata);
                hasFetchedInactiveRef.current = true;
                dataGridStore.setLoading(false);
            };

            getInactiveMetadata();
        }
    }, [showDeactivated]);

    useEffect(() => {
        const filtered = extendedMetadata.filter((field) => {
            const matchesSearch = field.name
                .toLowerCase()
                .includes(searchValue.toLowerCase());
            const matchesActiveFilter =
                showDeactivated || field.isActive !== false;
            return matchesSearch && matchesActiveFilter;
        });
        dataGridStore.setRows(filtered);
    }, [searchValue, extendedMetadata, showDeactivated]);

    const handleDeactivateMetadataFields = async () => {
        dataGridStore.setLoading(true);
        setIsDeleting(true);

        const selectedIds = dataGridStore.selectedRows.map((row) => row.id);
        let updatedList: ExtendedMetadataClassifier[] = [];

        await classifierService.deactivateExtendedMetadataClassifiers([
            ...selectedIds,
        ]);

        if (showDeactivated || hasFetchedInactiveRef.current) {
            updatedList = extendedMetadata.map((field) =>
                selectedIds.includes(field.id)
                    ? { ...field, isActive: false }
                    : field,
            );
        } else {
            updatedList = extendedMetadata.filter(
                (field) => !selectedIds.includes(field.id),
            );
        }
        setExtendedMetadata(updatedList);

        dataGridStore.removeAllSelectedRows();
        setShowDeleteConfirmation(false);
        setIsDeleting(false);
        dataGridStore.setLoading(false);
    };

    if (!canEditDataSources && !canViewMetadataManagement) {
        return (
            <Box sx={{ p: 4 }}>
                <Typography>You do not have access to this page</Typography>
            </Box>
        );
    }

    return (
        <Stack
            role="presentation"
            sx={{
                padding: 4,
                maxWidth: `calc(100% - ${theme.spacing(4)})`,
                width: "1200px",
                height: "calc(100vh - 128px)",
            }}
        >
            <OrganizationBreadcrumbs pageTitle="Metadata Field Management" />
            <Typography
                sx={{
                    fontSize: "24px",
                    fontWeight: "600",
                    color: theme.palette.gray[700],
                    mt: 2,
                }}
            >
                Metadata Field Management
            </Typography>

            <Stack direction="row" sx={{ mt: 2, mb: 0.5 }}>
                <AcxMainTextField
                    id="metadata-management-search"
                    value={searchValue}
                    placeholderText="Search metadata fields..."
                    onChange={(e) => setSearchValue(e.target.value)}
                    containerStyle={{
                        minWidth: "225px",
                        width: "20%",
                        marginRight: "auto",
                    }}
                    startAdornment={
                        <Search
                            htmlColor={theme.palette.gray[400]}
                            fontSize="small"
                            sx={{ marginRight: 1 }}
                        />
                    }
                />

                <FormControlLabel
                    control={
                        <AcxSwitch
                            checked={showDeactivated}
                            onChange={(e) =>
                                setShowDeactivated(e.target.checked)
                            }
                        />
                    }
                    label="Show deactivated"
                    sx={{
                        color: theme.palette.gray[600],
                    }}
                />

                <IconButton
                    disabled={!dataGridStore.selectedRows.length || isViewOnly}
                    sx={{
                        background: "transparent",
                        marginRight: 1,
                        padding: theme.spacing(0, 1),
                    }}
                    onClick={() => setShowDeleteConfirmation(true)}
                >
                    <DeleteOutline />
                </IconButton>

                <AddButton
                    color="primary"
                    onClick={() => {
                        setCurrentField(null);
                        setShowFieldModal(true);
                    }}
                    sx={{
                        height: "100%",
                        color: theme.palette.white.main,
                    }}
                    disabled={dataGridStore.isLoading || isViewOnly}
                >
                    Ext Metadata Field
                </AddButton>
            </Stack>
            <AcxDataGrid dataGridStore={dataGridStore} disableColumnResize />

            <AcxDialog
                title={"Deactivate Metadata Field"}
                text={`Are you sure you want to deactivate the selected metadata field${
                    dataGridStore.selectedRows.length > 1 ? "s" : ""
                }?`}
                isOpen={showDeleteConfirmation}
                onClose={() => setShowDeleteConfirmation(false)}
                contentWidth="400px"
                dialogContentChildren={
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                        }}
                    >
                        {dataGridStore.selectedRows.map((row) => (
                            <Typography
                                sx={{
                                    color: theme.palette.gray[500],
                                    fontWeight: 600,
                                }}
                                key={row.id}
                            >
                                - {row.name}
                            </Typography>
                        ))}
                    </Box>
                }
            >
                <AcxButton
                    color="primary"
                    loading={dataGridStore.isLoading || isDeleting}
                    disabled={isDeleting}
                    onClick={handleDeactivateMetadataFields}
                >
                    Delete
                </AcxButton>
            </AcxDialog>
            <ExtendedMetadataDialog
                open={showFieldModal}
                defaultValue={currentField ?? undefined}
                existingMetadata={extendedMetadata}
                onSave={handleFieldSaved}
                onClose={handleCloseDialog}
            />
        </Stack>
    );
};

const ObservableOrganizationMetadataManagement = observer(
    OrganizationMetadataManagement,
);

export default ObservableOrganizationMetadataManagement;
